'use client';

import { useContext, useState, type FC } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Select,
} from '@/_components';
import { updateAssignments } from '@/_api/admin-clients';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';

export const UpdateClientAssignmentsDialog: FC<{
  isOpen: boolean;
  onClose: () => void;
  selectedClientIds: number[];
  title: string;
}> = ({ isOpen, onClose, selectedClientIds, title }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newAssignedId, setNewAssignedId] = useState<string>('-1');

  const { addToast } = useContext(ToastContext);
  const { firmUserOptions } = useContext(FirmSettingsContext);
  const { loadUsersForTable } = useContext(CompanyContext);
  const { token } = useContext(UserContext);

  const onUpdate = async () => {
    try {
      setIsLoading(true);

      const { message, error } = await updateAssignments({
        token,
        advisorId: parseInt(newAssignedId),
        clientIds: selectedClientIds,
      });

      if (message === 'Success!') {
        loadUsersForTable();
        addToast('success', 'Assignments updated successfully');
      } else if (error) {
        addToast('error', error);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const options = [{ name: '', value: '-1' }, ...firmUserOptions];

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={title}>
      <Dialog.Content>
        <Flex gap={4} isColumn>
          <Alert type="warning">
            Updating the Assigned Firm User will update it for all of the
            user&apos;s entities.
          </Alert>

          <Select
            items={options}
            label="Assigned Firm User"
            isDisabled={isLoading}
            onChange={(e) => setNewAssignedId(e.target.value)}
            selectedValue={newAssignedId}
          />

          <ButtonGroup align="right">
            <Button
              intent="primary"
              isDisabled={isLoading}
              onClick={onUpdate}
              value="Update"
            />

            <Button
              intent="danger-secondary"
              isDisabled={isLoading}
              onClick={onClose}
              value="Cancel"
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
